import { useMemo, useState, useEffect, useRef } from "react";
import { usePrinterAlerts } from "@/hooks/usePrinterAlerts";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/useIsMobile";
import { BarChart3, Boxes, LayoutDashboard, ChefHat, FileClock, LayoutGrid, LogOut, Moon, ReceiptText, ScrollText, Settings2, ShoppingBag, Sun, TabletSmartphone, Truck, UsersRound, Wallet, UserRoundSearch, Wifi, Menu as MenuIcon, CalendarCheck, ClipboardCheck } from "lucide-react";
import { usePOS } from "@/context/POSContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import OfflineStatus from "@/components/OfflineStatus";
import UnifiedMobileDrawer from "@/components/UnifiedMobileDrawer";
import ConnectMyTelegramButton from "@/components/ConnectMyTelegram";
import { useSignOut } from "@/components/SignOutDialog";
import { CaptainAttendanceWidget } from "@/components/captain/CaptainAttendanceWidget";
import { AttendancePrompts } from "@/components/captain/AttendancePrompts";
import { AttendanceProvider } from "@/hooks/useAttendance";
import { SCREEN_PERMISSION, screenAllowed } from "@/lib/access";
import { useReportShortcuts } from "@/hooks/useReportShortcuts";
import { usePendingApprovals } from "@/hooks/usePendingApprovals";

// A link to a screen with its own switch follows the switch (lib/access.js); the
// rest follow the role. Shared with the captain shell's drawer and sidebar.
export const linkAllowed = (link, user, permissions) => {
  const page = link.to.slice(1);
  if (SCREEN_PERMISSION[page]) return screenAllowed(page, user, permissions, link.roles);
  return link.roles.includes(user?.role);
};

// Roles must match the backend user_role enum (admin|captain|cashier)
export const TOPBAR_HEIGHT = 64;
export const MOBILE_NAV_HEIGHT = 64;

export const routeGroups = [
  {
    name: "Operations",
    links: [
      { to: "/billing", label: "POS Terminal", icon: ReceiptText, roles: ["admin", "cashier"] },
      // The live board and its ledger are two pages under one prefix. `end`
      // keeps the board's link from lighting up on the ledger's URL; `short`
      // is the word the 64px mobile tab shows (the first word of the label
      // used to be "Running", which names nothing).
      { to: "/orders", end: true, label: "Running Orders/KOT", short: "Orders", icon: ShoppingBag, roles: ["admin", "cashier"] },
      { to: "/orders/history", label: "Orders/KOT History", short: "History", icon: FileClock, roles: ["admin", "cashier"] },
    ]
  },
  {
    // Back in the menu (owner, 2026-10-31: "kds is enabled and yet it is not coming in
    // nav"); dropped in the 2026-08-21 nav pass. Each follows its own switch
    // (pages.captain / pages.kds) through linkAllowed, like every screen here.
    name: "Devices",
    links: [
      { to: "/captain", label: "CaptainPAD", icon: TabletSmartphone, roles: ["admin", "captain"] },
      { to: "/kds", label: "Kitchen KDS", icon: ChefHat, roles: ["admin", "cashier", "captain"] },
    ]
  },
  {
    name: "Management",
    links: [
      { to: "/dashboard", label: "Owner dashboard", short: "Dashboard", icon: LayoutDashboard, roles: ["admin"] },
      { to: "/inventory", label: "Inventory", icon: Boxes, roles: ["admin"] },
      { to: "/purchase", label: "Purchase", icon: Truck, roles: ["admin"] },
      { to: "/approvals", label: "Approvals", icon: ClipboardCheck, roles: ["admin"] },
      { to: "/register", label: "Register", icon: Wallet, roles: ["admin", "cashier"] },
      { to: "/crm", label: "CRM", icon: UserRoundSearch, roles: ["admin", "cashier"] },
      { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "cashier"] },
      { to: "/staff", end: true, label: "Staff", icon: UsersRound, roles: ["admin"] },
      { to: "/staff/attendance", label: "Attendance", icon: CalendarCheck, roles: ["admin"] },
    ]
  },
  {
    name: "Administration",
    links: [
      { to: "/admin", label: "Settings", icon: Settings2, roles: ["admin"] },
      { to: "/logging", label: "Logging", icon: ScrollText, roles: ["admin", "cashier"] },
    ]
  }
];

export default function AppShell({ children }) {
  const { user, logout, theme, setTheme, settings, syncStatus, permissions, token, checkServer } = usePOS();
  const navigate = useNavigate();
  const location = useLocation();
  useReportShortcuts();
  usePrinterAlerts();
  const isCompactNav = useIsMobile(1023);
  const isMobile = useIsMobile(767);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);
  useEffect(() => {
    const handler = () => {
      if (window.innerWidth < 1024) {
        setMobileMenuOpen(p => !p);
      } else {
        setDesktopSidebarOpen(p => !p);
      }
    };
    window.addEventListener("toggle-sidebar", handler);
    return () => window.removeEventListener("toggle-sidebar", handler);
  }, []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Below 1280px the bill screen needs every column it has (categories, menu,
  // cart); with the 240px sidebar an 820px tablet left the menu ~40px wide
  // (owner, 2026-10-25). It starts hidden there; from 1024px the header menu
  // button shows it, and the Tables link always leads back.
  const narrowScreen = useIsMobile(1279);
  const onTerminal = location.pathname.startsWith("/billing/table/");
  // Settings brings its own navigation column; with the app sidebar beside it an
  // 820px tablet left settings ~350px and wrapped every description a word a line
  // (screen review 2026-10-25 #3). Same rule as the bill screen, and the sidebar
  // comes back on leaving if it was this rule that hid it.
  const ownNav = onTerminal || location.pathname.startsWith("/admin");
  const autoHidden = useRef(false);
  // Restores on leaving, or on widening past 1280px, only if this rule hid it —
  // a sidebar the operator closed themself stays closed.
  useEffect(() => {
    if (ownNav && narrowScreen) {
      setDesktopSidebarOpen(open => { if (open) autoHidden.current = true; return false; });
    } else if (autoHidden.current) {
      autoHidden.current = false; setDesktopSidebarOpen(true);
    }
  }, [ownNav, narrowScreen]);
  const { signOut: exit, dialog: signOutDialog } = useSignOut(token, () => { logout(); navigate("/login"); });
  // Phones: every tab strip scrolls sideways, and a tapped tab that was half off
  // screen stayed there, so nobody could see which tab was open (owner,
  // 2026-10-25). Bring whatever was tapped inside a sideways strip into view.
  useEffect(() => {
    const onClick = e => {
      const target = e.target.closest?.("button, a, [role=tab]");
      const strip = target?.parentElement;
      if (!strip || strip.scrollWidth <= strip.clientWidth + 1) return;
      if (!["auto", "scroll"].includes(getComputedStyle(strip).overflowX)) return;
      target.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const allLinks = useMemo(() => routeGroups.flatMap(g => g.links), []);
  // Longest matching prefix, not first: "/orders/history" must resolve to the
  // ledger's label in the topbar, not to "/orders" because it was listed first.
  const activeLink = allLinks
    .filter(link => location.pathname === link.to || location.pathname.startsWith(`${link.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];

  const visibleGroups = useMemo(() => {
    return routeGroups.map(group => ({
      ...group,
      links: group.links.filter(link => linkAllowed(link, user, permissions))
    })).filter(group => group.links.length > 0);
  }, [user, permissions]);

  const isOnline = syncStatus.mode !== 'offline';
  // The number beside Approvals: only for a person who may open it.
  const pendingApprovals = usePendingApprovals(token, screenAllowed("approvals", user, permissions, ["admin"]));

  return (
    <AttendanceProvider token={token}>
    <div className="min-h-[100dvh] h-[100dvh] w-full bg-background flex flex-col lg:flex-row overflow-hidden" data-testid="application-shell">
      
      {/* Desktop Sidebar */}
      {desktopSidebarOpen && (
        <aside className="hidden lg:flex flex-col w-60 border-r bg-card h-full shrink-0 z-50" data-testid="desktop-sidebar">
          {/* Brand */}
          {/* 56px, the header's height, so the two bottom borders run as one line (DESIGN.md §4). */}
          <div className="h-14 flex items-center px-4 border-b shrink-0 gap-2.5" data-testid="brand-identity">
            <div className="w-7 h-7 rounded-md bg-action flex items-center justify-center shrink-0">
              <span className="text-action-foreground font-semibold text-meta">RY</span>
            </div>
            <div className="flex flex-col leading-none">
              <strong className="text-lede font-semibold text-foreground">RestoYaar</strong>
              <span className="text-meta text-muted-foreground font-medium">POS</span>
            </div>
          </div>
        
          {/* Nav */}
          <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4" data-testid="primary-navigation">
            {visibleGroups.map((group, gi) => (
              <div key={group.name}>
                {gi > 0 && <div className="border-t border-border mb-3" />}
                <div className="px-3 text-meta font-medium text-muted-foreground mb-1">{group.name}</div>
                <div className="space-y-0.5">
                  {/* CaptainPAD is the phone app: on the PC it swapped this whole
                      layout for the pad's own (owner, 2026-11-01: "why is captainpad
                      showing on pc screen?"). It stays in the phone ☰ menu. */}
                  {group.links.filter(l => l.to !== "/captain").map(({ to, end, label, icon: Icon }) => (
                    <NavLink 
                      key={to} 
                      to={to} 
                      end={end}
                      data-testid={`nav-${label.toLowerCase().replaceAll(" ", "-")}`} 
                      title={label}
                      className={({isActive}) =>
                        `flex items-center gap-2.5 h-8 px-3 rounded-md text-body transition-colors relative ${
                          // Where you are is blue on a muted row; green is kept for
                          // commits, and side stripes are out (DESIGN.md §2.4, §4.5).
                          isActive
                            ? 'text-primary bg-muted font-semibold'
                            : 'text-muted-foreground font-medium hover:bg-muted/60 hover:text-foreground'
                        }`
                      }
                    >
                      <Icon className="w-4 h-4 shrink-0"/>
                      <span>{label}</span>
                      {to === "/approvals" && pendingApprovals > 0 && (
                        <span className="ml-auto min-w-5 rounded-full bg-action px-1.5 text-center text-micro font-semibold text-action-foreground tabular-nums"
                              data-testid="approvals-badge" aria-label={`${pendingApprovals} waiting`}>{pendingApprovals}</span>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        
          {/* Footer */}
          <div className="p-3 border-t flex items-center gap-2">
            {/* User pill */}
            <div className="flex items-center gap-2 flex-1 min-w-0 px-2 py-1.5 rounded-md hover:bg-muted transition-colors" title={user?.name}>
              <span className="w-7 h-7 shrink-0 rounded-full bg-info-subtle text-info font-medium flex items-center justify-center text-meta">
                {user?.name?.split(" ").map(n => n[0]).join("").slice(0,2)}
              </span>
              <div className="flex flex-col min-w-0">
                <span className="text-body font-medium text-foreground leading-tight truncate">{user?.name}</span>
                <span className="text-meta text-muted-foreground">{user?.role_name || (user?.role === user?.name ? 'Account' : user?.role)}</span>
              </div>
            </div>
            <ConnectMyTelegramButton
              token={token}
              iconOnly
              className="w-8 h-8 min-h-0 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center"
            />
            <button 
              onClick={() => setTheme(theme === "light" ? "dark" : "light")} 
              className="w-8 h-8 min-h-0 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center"
              data-testid="theme-toggle-button" 
              aria-label="Toggle colour theme"
            >
              {theme === "light" ? <Moon className="w-4 h-4"/> : <Sun className="w-4 h-4"/>}
            </button>
            <button 
              onClick={exit} 
              className="w-8 h-8 min-h-0 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors flex items-center justify-center"
              data-testid="logout-button" 
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4"/>
            </button>
          </div>
        </aside>
      )}

      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {/* Topbar */}
        {!location.pathname.startsWith("/billing") && <header className="h-14 flex items-center justify-between px-3 sm:px-4 md:px-5 bg-card border-b shrink-0 z-40" data-testid="application-topbar">
          <div className="flex items-center gap-2 min-w-0">
            {/* Phones & Tablets < 1024px: the ☰ menu button */}
            <button
              className="flex lg:hidden w-11 h-11 -ml-1 sm:-ml-2 items-center justify-center rounded-md hover:bg-muted text-foreground shrink-0"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-button"
            >
              <MenuIcon className="w-5 h-5" />
            </button>
            {/* Desktop >= 1024px: sidebar toggle */}
            <button
              className="hidden lg:flex w-11 h-11 -ml-2 items-center justify-center rounded-md hover:bg-muted text-muted-foreground shrink-0"
              onClick={() => setDesktopSidebarOpen(!desktopSidebarOpen)}
              aria-label="Toggle desktop sidebar"
              data-testid="sidebar-toggle-button"
            >
              <MenuIcon className="w-5 h-5" />
            </button>
            {/* Mobile/Tablet brand + current page title */}
            <div className="flex lg:hidden items-center gap-2 min-w-0">
              <div className="w-6 h-6 shrink-0 rounded-md bg-action flex items-center justify-center">
                <span className="text-action-foreground font-semibold text-micro">RY</span>
              </div>
              <strong className="text-lede font-semibold truncate" data-testid="mobile-page-title">{activeLink?.label || "RestoYaar"}</strong>
            </div>
            <div className="hidden lg:flex flex-col leading-none min-w-0">
              <div className="flex items-center gap-1.5 text-meta text-muted-foreground">
                <span>{visibleGroups.find(g => g.links.some(l => l.to === activeLink?.to))?.name || "Operations"}</span>
                <span className="text-muted-foreground/40 font-normal">/</span>
                <strong className="text-title font-semibold text-foreground truncate" data-testid="current-page-title">
                  {activeLink?.label || settings.restaurantName}
                </strong>
              </div>
              <span className="text-meta text-muted-foreground mt-0.5 truncate">{settings.restaurantName}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5 sm:gap-2.5 md:gap-3 shrink-0">
            {/* The person's own duty and break, on the billing PC too (attendance spec §6). */}
            <CaptainAttendanceWidget token={token} compact={isCompactNav} />
            {/* Sync status chip */}
            <span className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 rounded-full bg-muted/60 text-meta font-medium text-muted-foreground border border-border/60" data-testid="terminal-sync-status">
              <span className={`w-2 h-2 rounded-full shrink-0 ${isOnline ? 'bg-success' : 'bg-danger animate-pulse'}`} />
              {/* A word with the dot on every size: status is never colour alone (DESIGN.md §2.4 rule 4). */}
              <span className="hidden sm:inline max-w-[8.5rem] truncate sm:max-w-none">{syncStatus.label || (isOnline ? "Local database ready" : "Offline mode")}</span>
              <span className="sm:hidden text-[11px] font-semibold">{isOnline ? "Online" : "Offline"}</span>
            </span>
            
            {/* Desktop user */}
            <div className="hidden lg:flex items-center gap-2.5 pl-3 border-l" data-testid="signed-in-user">
              <span className="w-8 h-8 rounded-full bg-info-subtle text-info font-medium flex items-center justify-center text-meta">
                {user?.name?.split(" ").map(n => n[0]).join("").slice(0,2)}
              </span>
              <div className="flex flex-col leading-none">
                <strong className="text-body font-semibold text-foreground">{user?.name}</strong>
                <small className="text-meta text-muted-foreground">{user?.role_name || (user?.role === user?.name ? 'Account' : user?.role)}</small>
              </div>
            </div>
          </div>
        </header>}

        {/* Main Content Area */}
        {/* DESIGN.md §3: desktop pages are dense (32px controls). Billing is a touch
            surface and phones keep touch sizes, so only they stay out of `compact`. */}
        <main
          className="flex-1 flex flex-col relative min-h-0 min-w-0 w-full overflow-y-auto"
          data-testid={`page-${location.pathname.slice(1) || "home"}`}
          data-density={!isCompactNav && !location.pathname.startsWith("/billing") ? "compact" : undefined}
        >
          {children}
        </main>
        
        <OfflineStatus/>
      </div>

      <UnifiedMobileDrawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        groups={visibleGroups}
        user={user}
        permissions={permissions}
        theme={theme}
        setTheme={setTheme}
        onExit={exit}
        token={token}
        checkServer={checkServer}
      />
      {signOutDialog}
      {/* The split-break prompt and START DUTY bar, once per screen. */}
      <AttendancePrompts token={token} />
    </div>
    </AttendanceProvider>
  );
}

/**
 * The phone's one menu (owner 2026-11-01): every page the person may open, grouped as
 * on the desktop sidebar, then Connect my Telegram, the theme and sign-out. Replaces the
 * bottom bar + "More" sheet, which sat beside the captain screens' ☰ and confused staff.
 */
export function MobileMenu({ open, onOpenChange, ...props }) {
  return <UnifiedMobileDrawer open={open} onClose={() => onOpenChange(false)} {...props} />;
}
